import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { date } from "../../../../../agent/src/common/time";
import ConversationTimeline, { type TimelineItem } from "../../leads/chat/ConversationTimeline";
import { type RouterOutputs } from "../../../trpc";
import { lead } from "../../../types";
import { trpc } from "../../../trpc";
import { usePipeline } from "../../../contexts/PipelineContext";
import { useNavigate } from "react-router-dom";

type AppointmentDetail = RouterOutputs["appointments"]["get"];

interface AppointmentDetailPanelProps {
  appointment?: AppointmentDetail;
  leadData?: lead;
  isLoading: boolean;
  merchantId: string;
}

const AppointmentDetailPanel = ({
  appointment,
  isLoading,
  merchantId,
}: AppointmentDetailPanelProps) => {
  const { allPositions } = usePipeline();
  const navigate = useNavigate();

  // Fetch conversation timeline
  const { data: timelineData, isLoading: isLoadingTimeline } =
    trpc.conversations.listConversationTimeline.useQuery(
      { merchantId, leadId: appointment?.leadId || "" },
      {
        enabled: !!merchantId && !!appointment?.leadId,
      }
    );

  const timelineItems: TimelineItem[] = (timelineData as TimelineItem[] | undefined) || [];

  const handleGoToLead = () => {
    if (appointment?.leadId) {
      navigate(`/inbox?id=${appointment.leadId}`);
    }
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="flex flex-col gap-4">
          <Skeleton className="h-[200px]" />
          <Skeleton className="h-[400px]" />
        </div>
      </div>
    );
  }

  if (!appointment) {
    return null;
  }

  return (
    <div className="flex h-full flex-row overflow-hidden">
      {/* Left Panel: Appointment Details */}
      <div className="w-full shrink-0 overflow-y-auto border-r border-gray-200 bg-white md:min-w-[380px] lg:w-[380px]">
        <div className="p-6">
          <div className="flex flex-col gap-4">
            <div>
              <h2 className="mb-2 text-xl font-bold">
                Cita #{appointment.id.slice(0, 8)}
              </h2>
              <p className="mb-4 text-xs text-gray-500">
                {date(appointment.createdAt).format("DD/MM/YYYY HH:mm")}
              </p>

              <div className="mb-4 flex flex-wrap gap-2">
                <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800">
                  {appointment.date}
                </span>
                <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800">
                  {appointment.time}
                </span>
              </div>
            </div>

            <Separator />

            {/* Property Information */}
            <div>
              <p className="mb-3 text-sm font-semibold text-gray-700">
                Información del Inmueble
              </p>
              <div className="flex flex-col gap-2">
                {appointment.listingCode && appointment.listingCode !== "NONE" && (
                  <div>
                    <p className="mb-1 text-xs text-gray-600">
                      Código de Listado:
                    </p>
                    <p className="text-sm font-semibold">
                      {appointment.listingCode}
                    </p>
                  </div>
                )}
                {appointment.projectCode && (
                  <div>
                    <p className="mb-1 text-xs text-gray-600">
                      Código de Proyecto:
                    </p>
                    <p className="text-sm font-semibold">
                      {appointment.projectCode}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Employee Information */}
            {appointment.employeeName && (
              <div>
                <p className="mb-3 text-sm font-semibold text-gray-700">
                  Empleado Asignado
                </p>
                <p className="text-sm font-semibold">
                  {appointment.employeeName}
                </p>
              </div>
            )}

            <Separator />

            {/* Lead Information */}
            <div>
              <p className="mb-3 text-sm font-semibold text-gray-700">
                Información del Prospecto
              </p>
              <div className="flex flex-col gap-2">
                <div>
                  <p className="mb-1 text-xs text-gray-600">
                    Nombre:
                  </p>
                  <p className="text-sm font-semibold">
                    {appointment.leadName}
                  </p>
                </div>
                {appointment.leadPhone && (
                  <div>
                    <p className="mb-1 text-xs text-gray-600">
                      Teléfono:
                    </p>
                    <p className="text-sm font-semibold">
                      {appointment.leadPhone}
                    </p>
                  </div>
                )}
              </div>

              {/* Button to navigate to lead */}
              <Button
                className="mt-4 w-full"
                size="sm"
                onClick={handleGoToLead}
              >
                Ir a Prospecto
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel: Conversation Interface */}
      <div className="flex min-w-0 flex-1 flex-col overflow-y-auto bg-[#F9F9FB]">
        <div className="p-4">
          <div className="rounded-md bg-white shadow-[0px_4px_20px_1px_rgba(125,124,135,0.2)]">
            {appointment.leadId ? (
              <ConversationTimeline
                timelineData={timelineItems}
                isLoading={isLoadingTimeline}
                allPositions={allPositions}
              />
            ) : (
              <div className="flex h-full items-center justify-center">
                <p className="text-gray-500">No hay conversación disponible</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentDetailPanel;
